import React, { useEffect, useMemo, useState } from 'react'
import {
  Skeleton,
  Table as TableCortex,
  Td,
  THead,
  TRow
} from '@tecsinapse/cortex-react'
import { Trans } from '@lingui/react/macro'
import {
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  useReactTable
} from '@tanstack/react-table'
import { gql } from '@apollo/client'

import { columnsBuscaArquivos } from './columns-busca-arquivos'
import { rowsPerPageOptions } from '@/core/utils/rows-per-page'
import { Table } from '@/core/components/index'
import { ColumnsProps } from '../types/types'
import { ItemPastaFragment } from '../data/item-pasta-fragment'
import { GetPastaQuery } from '@/core/types/graphql'
import { useParams } from 'react-router'
import { ItemBibliotecaHomeCardType } from './item-biblioteca-home-card'
import { useTabelaBuscaArquivo } from '../hooks/use-tabela-busca-arquivo'

interface TabelaPastasPropsProps extends ColumnsProps {
  searchText: string
  pastaAtual: GetPastaQuery['pasta']
}

export const BUSCA_QUERY = gql`
  query BuscaBibliotecaPage($filterInput: ItemBibliotecaFilterInput!) {
    buscaBibliotecaPaged(filterInput: $filterInput) {
      content {
        ...ItemPastaFragment
      }
      totalElements
    }
  }
  ${ItemPastaFragment}
`

const initialFilters = [
  {
    dataCriacao: new Date(),
    nome: '',
    proprietario: {
      email: ''
    },
    pastaPai: {
      nome: ''
    }
  }
]

export const TabelaBuscaArquivo = ({
  onClickItem,
  selectedRows,
  isItemSelected,
  onDoubleClick,
  searchText
}: TabelaPastasPropsProps) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: rowsPerPageOptions[0]
  })
  const [filteredItems, setFilteredItems] =
    useState<ItemBibliotecaHomeCardType[]>(initialFilters)
  const params = useParams()

  const { buscaBibliotecaPaged, loading } = useTabelaBuscaArquivo({
    pagination,
    searchText
  })

  const fetchedItems = useMemo(
    () => buscaBibliotecaPaged?.content ?? [],
    [buscaBibliotecaPaged]
  )

  useEffect(() => {
    setFilteredItems(() => {
      if (params.idPasta) {
        return fetchedItems.filter(
          item => item?.pastaPai?.id === params.idPasta
        )
      }
      return fetchedItems
    })
  }, [fetchedItems])

  const totalElements = useMemo(
    () => filteredItems?.length ?? 0,
    [filteredItems]
  )

  const table = useReactTable({
    data: filteredItems,
    columns: columnsBuscaArquivos({
      isItemSelected,
      selectedRows,
      onClickItem
    }),
    getCoreRowModel: getCoreRowModel(),
    rowCount: totalElements,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
    state: {
      pagination
    }
  })

  return (
    <>
      {loading ? (
        <Skeleton className='h-28 w-full rounded-mili' />
      ) : (
        <TableCortex className='w-full mb-deca'>
          <THead>
            <TRow>
              <Td colSpan={99} className={'py-centi'}>
                <div
                  className={
                    'flex w-full flex-row items-center justify-between'
                  }
                >
                  <p className='text-h3 font-bold'>
                    <Trans>Pastas/Arquivos</Trans>
                  </p>
                </div>
              </Td>
            </TRow>
          </THead>
          <Table.Body
            table={table}
            onClickRow={onClickItem}
            onDoubleClickRow={onDoubleClick}
            loading={loading}
          />
          <Table.Footer
            table={table}
            rowsPerPageOptions={rowsPerPageOptions}
            loading={loading}
            countList={totalElements}
          />
        </TableCortex>
      )}
    </>
  )
}
